import { Button, Group, Title, Tooltip } from "@mantine/core";
import { IconMailFast } from "@tabler/icons-react";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodResolver } from "mantine-form-zod-resolver";
import { httpPostGraphql } from "shared/api/httpClient";
import { Content } from "shared/components/global/Content";
import { SplitButton } from "shared/components/global/SplitButton";
import {
    ComposeFormProvider,
    composeFormStateSchema,
    composeFormStateToDraftInfoInput,
    createComposeFormState,
    useComposeForm,
} from "shared/components/message/compose/context";
import { useComposeFormPermissionChecker } from "shared/components/message/compose/useComposeFormPermissionChecker";
import { ComposeForm } from "shared/components/message/ComposeForm";
import { useComposeFormActions } from "shared/components/message/useComposeFormActions";
import { useMessageActions } from "shared/components/message/useMessageActions";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { fail, voiding } from "shared/utils/fns";
import { some } from "shared/utils/maybe";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";

const Component = () => {
    const nav = useNavigate();
    const { id } = Route.useParams();
    const { data } = useSuspenseQuery(queries.msg(id));

    const saveDraft = useGraphqlMutation(mutations.saveDraft);

    const form = useComposeForm({
        initialValues: createComposeFormState({ ...data, audience: { geofilters: [], geometries: [], groups: [], topics: [], users: [] } }),
        validate: zodResolver(composeFormStateSchema),
    });

    const actions = useMessageActions();
    const { openSendModal } = useComposeFormActions(form);

    const permissionChecker = useComposeFormPermissionChecker(form);

    function handleSaveAsDraft() {
        some(form)
            .if((it) => !it.validate().hasErrors)
            ?.map((it) => it.getValues())
            .map(composeFormStateToDraftInfoInput.parse)
            .binding("params")
            .take(saveDraft.mutateAsync)
            .then(({ draft: { id } }) => nav({ to: "/messages/drafts/$id", params: { id } }))
            .then(() => notify.show.success("Draft Saved!"))
            .catch(notify.catch);
    }

    function handleReview() {
        if (form.validate().hasErrors) return;
        const params = composeFormStateToDraftInfoInput.parse(form.getValues());

        openSendModal({
            async getMessage() {
                const { draft } = await saveDraft.mutateAsync({ params });
                return draft;
            },
        });
    }

    return (
        <Content paper>
            <Content.Heading backable>
                <Title order={3}>Forward Message</Title>
            </Content.Heading>

            <ComposeFormProvider form={form}>
                <ComposeForm />
            </ComposeFormProvider>

            <Tooltip {...permissionChecker.getTooltipProps()}>
                <Group justify="flex-end" p="xs" w="min-content" wrap="nowrap" ml="auto">
                    <Button
                        variant="default"
                        onClick={handleSaveAsDraft}
                        disabled={permissionChecker.hasErrors}
                        loading={saveDraft.isPending || saveDraft.isSuccess}
                    >
                        Save as Draft
                    </Button>
                    <SplitButton
                        menu={{
                            "Send Preview": { fn: () => actions.openPreviewModal({ form }) },
                            "Schedule for Later": {
                                fn: async () => {
                                    if (form.validate().hasErrors) return;
                                    await actions.openScheduleDeliveryModal({
                                        isDisabled: () =>
                                            Object.values(form.getValues().audience).every((part) => part.length == 0)
                                                ? "You must specify an audience."
                                                : null,
                                        async getMessage() {
                                            if (form.validate().hasErrors) return fail("Form has errors.");

                                            const { draft } = await saveDraft.mutateAsync({
                                                params: composeFormStateToDraftInfoInput.parse(form.getValues()),
                                            });

                                            return draft;
                                        },
                                    });

                                    await nav({ to: "/messages/scheduled" });
                                },
                            },
                        }}
                        onClick={voiding(handleReview)}
                        disabled={permissionChecker.hasErrors}
                        leftSection={<IconMailFast size={16} />}
                    >
                        Review & Send
                    </SplitButton>
                </Group>
            </Tooltip>
        </Content>
    );
};

const queries = {
    msg: (id: string) =>
        queryOptions({
            queryKey: qk("message", "forward", id),
            queryFn: () =>
                httpPostGraphql(
                    graphql(`
                        query MessageFwdComposeDetails($id: UUID!) {
                            message(id: $id) {
                                ...ComposeDetails
                            }
                        }
                    `),
                    { id },
                ),
            select: ({ message }) => message,
        }),
};

const mutations = {
    saveDraft: graphql(`
        mutation MessageForwardComposeSend($params: DraftInfoInput!) {
            draft: createDraft(params: $params) {
                id
            }
        }
    `),
    discardDraft: graphql(`
        mutation MessageForwardComposeDiscardDraft($id: UUID!) {
            discardMessageDraft(id: $id) {
                id
            }
        }
    `),
    send: graphql(`
        mutation MessageForward($id: UUID!, $expireAt: Instant) {
            sendMessage(id: $id, expireAt: $expireAt) {
                id
            }
        }
    `),
};

/** @public */
export const Route = createFileRoute("/_auth/messages/$id/fwd")({
    component: Component,
});
